/**
 * Names and locates Neko's deployment regions for the clinician.
 *
 * Neko runs one backend per region, and a member's data lives in exactly one of them.
 * The backend states both facts: `GET /api/v1/config` gives `region`, the region serving
 * this app, and `PatientDetails.dataRegion` gives the region holding the member's data.
 * Everything here turns those two deployment labels into something a clinician can read
 * and act on.
 *
 * The tables below mirror `Cur8HostEnvironment.FrontendStampsByDeploymentLabel` and
 * `Api.Admin`'s `DeploymentLabelNames` configuration in APIBackend. A label missing from
 * them still renders, upper-cased, so a newly added region degrades to a legible name
 * rather than a blank.
 */

export type DeploymentLabel = string;

/**
 * How far the app has got in learning which region serves it.
 *
 * `resolving` and `unavailable` are separate states on purpose. Code deciding whether to
 * withhold a member's record must wait out `resolving`; treating it as "no region" would
 * render the record for as long as that request takes, which is the exposure the guardrail
 * exists to close.
 */
export type RegionResolution =
  | { status: "resolving" }
  | { status: "resolved"; servingRegion: DeploymentLabel }
  /** The request failed, or this deployment states no region. */
  | { status: "unavailable" };

type RegionNames = {
  /** With the stage, to tell two deployments in one data-residency region apart. */
  displayName: string;
  /** The data-residency region alone, which is what a clinician reasons about. */
  regionName: string;
  /** Stage this label is deployed on, as `config.json` reports it in `stageName`. */
  stage: "dev" | "prod";
  /** Where this region's DermaUI is served. Every entry has been confirmed to respond. */
  url: string;
};

const REGIONS: Record<DeploymentLabel, RegionNames> = {
  dev: {
    displayName: "Dev EU",
    regionName: "EU",
    stage: "dev",
    url: "https://derma.neu.dc.dev.cur8.co",
  },
  devcusd1: {
    displayName: "Dev US",
    regionName: "US",
    stage: "dev",
    url: "https://derma.cusd1.dc.dev.cur8.co",
  },
  prodneu: {
    displayName: "Prod EU",
    regionName: "EU",
    stage: "prod",
    url: "https://derma.neu.dc.cur8.co",
  },
  prodeusd1: {
    displayName: "Prod US",
    regionName: "US",
    stage: "prod",
    url: "https://derma.eusd1.dc.cur8.co",
  },
};

/** Whether this app knows the given deployment label at all, and so where its DermaUI is served. */
export function isKnownRegion(label: DeploymentLabel) {
  return label in REGIONS;
}

/** The data-residency region of a deployment label: "EU", "US". */
export function regionName(label: DeploymentLabel) {
  return REGIONS[label]?.regionName ?? label.toUpperCase();
}

/**
 * Names for two labels a message has to tell apart. Falls back to the stage-and-region
 * name when both labels sit in one data-residency region, where the residency names
 * alone would read "held in EU, you are working in EU".
 */
export function namesThatDistinguish(
  first: DeploymentLabel,
  second: DeploymentLabel
) {
  const firstName = regionName(first);
  const secondName = regionName(second);

  if (first !== second && firstName === secondName) {
    return {
      first: REGIONS[first]?.displayName ?? firstName,
      second: REGIONS[second]?.displayName ?? secondName,
    };
  }

  return { first: firstName, second: secondName };
}

/**
 * Where the given region's DermaUI serves the given path, or undefined when this
 * environment cannot reach it. Preprod and pull-request deployments serve every region
 * from one instance, so linking a clinician to a prod or dev host from one of them would
 * take them somewhere their session does not reach; the caller says so instead of
 * offering a link that misleads.
 */
export function dermaUrlInRegion(
  label: DeploymentLabel,
  stageName: string,
  path: string
) {
  const region = REGIONS[label];
  if (!region || region.stage !== stageName) {
    return undefined;
  }

  return `${region.url}${path}`;
}
